import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import NotifStack from "../../utils/NotifStack";
import { Breakpoints } from "../../utils/breakpoints";
import { initSectionThemeTriggers } from "../../utils/sectionThemeTriggers";
import { initFooterClamp } from "../../utils/footerClamp";
import { observeMediaElement } from "../../utils/mediaTelemetry";
import { initBgVideoOnView, loadBgVideo } from "../../utils/bgVideo";
import PageTransition from "../global/page-transition";

// Sync with the hero's own intro reveal (see Anim.js GROUP_DELAY + the
// hero title's own data-anim-delay on page-lifetime.twig).
const ENTRANCE_DELAY = 0.5;
const ENTRANCE_GAP = 24;

// When the progress bar starts filling — slightly before the card's own
// entrance settles (NotifStack: 1s delay + 0.6s slide; inline: see above).
const PROGRESS_DELAY_STICKY = 1.3;
const PROGRESS_DELAY_INLINE = ENTRANCE_DELAY + 0.4;

export default class LifetimeStickyPush {
  constructor(el) {
    this.el = el;
    this.closeBtn = el.querySelector(".c-lifetime-sticky-push__close");
    this.isInline = el.hasAttribute("data-inline");
    this.url = el.dataset.lifetimeUrl || "";

    const media = el.querySelector("media-player, video");
    if (media) {
      observeMediaElement(media, {
        context: "lifetime-sticky-push",
        kind: this.isInline ? "inline" : "sticky",
        src: media.getAttribute("src") || media.dataset.src,
      });
      if (media.tagName === "MEDIA-PLAYER") {
        import("vidstack/player");
      } else {
        // Loaded right away rather than when it scrolls into view: the push is
        // on screen from the start (just not revealed yet). Play/pause still
        // follows visibility.
        loadBgVideo(media);
        initBgVideoOnView(media);
      }
    }

    // The whole card links to the lifetime page — except when we're already
    // on it (nothing to navigate to) or when the click lands on the close btn.
    if (this.url && !this._isCurrentPage(this.url)) {
      this.el.classList.add("is-clickable");
      this.el.addEventListener("click", (e) => this._handleClick(e));
    }

    if (this.isInline) {
      this.closeBtn.addEventListener("click", () => this._dismissInline());
      this._initInlineSticky();
      this._initInlineEntrance();
    } else {
      NotifStack.register(this.el);
      this.closeBtn.addEventListener("click", () => NotifStack.dismiss(this.el));
    }
    this.footerClamp = initFooterClamp(this.el);
    this._initThemeTriggers();
    this._initProgressReveal(
      this.isInline ? PROGRESS_DELAY_INLINE : PROGRESS_DELAY_STICKY
    );
  }

  // Fills the tiers left to right: sold-out tiers chain quickly, then the
  // current tier decelerates to its level while its counter ticks up. The
  // shimmer (CSS) only starts once .is-filled is set. Clip-path rather than
  // width so the count label and the fill's padding don't reflow mid-tween.
  _initProgressReveal(delay) {
    const fills = [...this.el.querySelectorAll(".c-lifetime-sticky-push__fill")];
    if (!fills.length) return;

    this._initShimmerTrack(fills);

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.el.classList.add("is-filled");
      return;
    }

    const tl = gsap.timeline({
      delay,
      onComplete: () => this.el.classList.add("is-filled"),
    });

    fills.forEach((fill) => {
      const isCurrent = fill.classList.contains("c-lifetime-sticky-push__fill--current");

      tl.fromTo(
        fill,
        { clipPath: "inset(0% 100% 0% 0% round 8px)" },
        {
          clipPath: "inset(0% 0% 0% 0% round 8px)",
          duration: isCurrent ? 1.2 : 0.35,
          ease: isCurrent ? "power3.out" : "none",
          // The count label may overflow a narrow fill — don't keep it clipped.
          clearProps: "clipPath",
        }
      );

      const count = isCurrent && fill.querySelector("[data-count]");
      if (count) {
        const target = Number(count.dataset.count);
        const total = count.dataset.total;
        const counter = { value: 0 };
        tl.to(
          counter,
          {
            value: target,
            duration: 1.2,
            ease: "power3.out",
            onUpdate: () => {
              count.textContent = `${Math.round(counter.value)}/${total}`;
            },
          },
          "<"
        );
      }
    });
  }

  // Sticks the card under the navbar for the rest of the page once scroll
  // carries it past that point, instead of scrolling away with the hero.
  // The card is already position:absolute (out of flow, see _lifetime.scss)
  // so there's no flow-space to reserve — a plain class toggle to `fixed`
  // does the job without GSAP's pin-spacer wrapper.
  _initInlineSticky() {
    Breakpoints.desktop(() => {
      this.stickyTrigger = ScrollTrigger.create({
        trigger: this.el,
        start: "top 120px",
        end: "max",
        onEnter: () => this.el.classList.add("is-stuck"),
        onLeaveBack: () => this.el.classList.remove("is-stuck"),
      });

      return () => {
        this.stickyTrigger = null;
        // .is-stuck (position: fixed) isn't scoped to desktop in the CSS, so it
        // must not outlive the trigger that manages it.
        this.el.classList.remove("is-stuck");
      };
    });
  }

  // Non-inline cards already slide in from the right via NotifStack. The
  // inline variant is left-anchored (see .c-lifetime-sticky-push[data-inline]
  // in _lifetime.scss), so it should enter from the left instead — same
  // off-screen-x-offset idiom as ModalCollectionPush.initEntrance.
  _initInlineEntrance() {
    gsap.from(this.el, {
      x: -(this.el.offsetWidth + ENTRANCE_GAP),
      duration: 1,
      ease: "expo.out",
      delay: ENTRANCE_DELAY,
    });
  }

  // Feeds the shimmer CSS (see &__fill::after) the geometry it needs to draw
  // one continuous sweep across every fill: --track on the progress wrapper,
  // --x on each fill. Re-measured on resize (the card is fluid on mobile).
  _initShimmerTrack(fills) {
    const progress = this.el.querySelector(".c-lifetime-sticky-push__progress");
    if (!progress) return;

    const measure = () => {
      const origin = progress.getBoundingClientRect().left;
      // Rects ignore clip-path, so this is safe mid-reveal.
      const end = fills[fills.length - 1].getBoundingClientRect().right;
      progress.style.setProperty("--track", end - origin);
      fills.forEach((fill) => {
        fill.style.setProperty("--x", fill.getBoundingClientRect().left - origin);
      });
    };

    measure();
    new ResizeObserver(measure).observe(progress);
  }

  _dismissInline() {
    this.stickyTrigger?.kill();
    this.footerClamp?.kill();
    this.el.classList.remove("is-stuck");

    gsap.to(this.el, {
      autoAlpha: 0,
      duration: 0.2,
      ease: "power3.inOut",
      onComplete: () => {
        this.el.style.display = "none";
      },
    });
  }

  _handleClick(e) {
    if (e.target.closest(".c-lifetime-sticky-push__close")) return;
    PageTransition.go(this.url, e, this.el);
  }

  _isCurrentPage(url) {
    try {
      const target = new URL(url, window.location.origin);
      const strip = (p) => p.replace(/\/+$/, "");
      return (
        target.origin === window.location.origin &&
        strip(target.pathname) === strip(window.location.pathname)
      );
    } catch {
      return false;
    }
  }

  _initThemeTriggers() {
    // Reads the card's live rect each tick, so it stays in sync with the
    // footer clamp's translateY and with inline stuck / unstuck positioning.
    initSectionThemeTriggers(this.el);
  }
}
